import { Grid, Tooltip, useMediaQuery } from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import theme from "Theme/AppTheme";
import SoundClipEditorV2 from "components/SoundClipEditor/SoundClipEditorV2";
import { AcxTranscription } from "components/UI/AcxTranscription";
import { Observer } from "mobx-react";
import React, { FunctionComponent, useEffect, useState } from "react";
import Scrollbars from "react-custom-scrollbars-2";
import LocalStorage from "stores/LocalStorage";
import RootStore from "stores/RootStore";
import { InteractionType } from "../../../models/InteractionType";
import { PlayerOptions } from "../../SoundClipEditor/SoundClipEditor";
import AcxChatViewer from "../../UI/Chat/AcxChatViewer";
import AcxDrawer from "../../UI/Drawer/AcxDrawer";
import { EvalStore } from "../Stores/EvalStore";
import hexToRGB from "utils/hexToRGB";
import {
    ENGLISH_LANGUAGE_CODE,
    UNREDACTED_ENGLISH_LANGUAGE_CODE,
} from "constants/TranscriptLanguageOptions";

interface OwnProps {
    store: EvalStore;
    collapsible?: boolean;
    altWidth?: number;
    altLeftPosition?: number;
    miniHeight: number;
    maxHeight: number;
    canSeeTranscript: boolean;
}

const soundEditorMinSizeUltraUser = 64;
const soundEditorMaxSizeUltraUser = 195;

type Props = OwnProps;

const MediaViewerDock: FunctionComponent<Props> = (props) => {
    const [soundEditorOpen, setSoundEditorOpen] = useState(true);
    const [playerOptions, setPlayerOptions] = useState<PlayerOptions>({
        id: "0",
        isPlaying: false,
        startTime: 0,
        endTime: 0,
    });

    const onSelectedClip = (options: PlayerOptions) => {
        options &&
            setPlayerOptions((prev) => ({
                ...prev,
                id: options.id,
                isPlaying: options.isPlaying,
                startTime: options.startTime,
                endTime: options.endTime,
            }));
    };

    const isAudio =
        props.store.evalAudio &&
        props.store.mediaType === InteractionType.Audio;
    const isProcessedChat =
        props.store.mediaType === InteractionType.ProcessedChat &&
        !!props.store.evalChat;
    const showChatUnredactedUI =
        props.canSeeTranscript &&
        props.store.unredactedChatTranscript !== undefined;
    const showChatTranslationUI =
        props.canSeeTranscript &&
        !showChatUnredactedUI &&
        props.store.translatedChatTranscript !== undefined;

    useEffect(() => {
        if (isAudio && props.canSeeTranscript)
            props.store.toggleMediaViewerDrawer();
    }, [isAudio, props.canSeeTranscript, props.store]);

    const soundEditorOpenToggle = () => {
        if (!props.canSeeTranscript || !isAudio)
            props.store.toggleMediaViewerDrawer();
        else setSoundEditorOpen((current) => !current);
    };

    const audioCollapseOpenCheck =
        isAudio && props.canSeeTranscript
            ? soundEditorOpen
            : props.store.isMediaViewerDockOpen;

    const fullheight =
        !isAudio || !props.canSeeTranscript
            ? { height: "100%" }
            : { height: "auto" };

    let editorHeight = soundEditorOpen
        ? soundEditorMaxSizeUltraUser
        : soundEditorMinSizeUltraUser;

    if (!props.canSeeTranscript) {
        editorHeight = props.store.isMediaViewerDockOpen
            ? props.maxHeight - 2
            : props.collapsible
            ? props.miniHeight - 2
            : 0;
    }

    let drawerMinSize = props.miniHeight;

    if (
        !props.store.isMediaViewerDockOpen &&
        !soundEditorOpen &&
        props.canSeeTranscript
    ) {
        drawerMinSize = soundEditorMinSizeUltraUser + 25;
    } else if (
        !props.store.isMediaViewerDockOpen &&
        soundEditorOpen &&
        props.canSeeTranscript &&
        isAudio
    ) {
        drawerMinSize = soundEditorMaxSizeUltraUser + 1;
    }

    const handleOpenTranscript = () => {
        const local = RootStore().getStore(LocalStorage);

        const tempLocal = local.getLocalStore();

        tempLocal.getItem("transcriptOpened").then((val) => {
            if (!val && isAudio && props.canSeeTranscript) {
                tempLocal.setItem("transcriptOpened", true);
            }
        });

        props.store.toggleMediaViewerDrawer();
    };

    useEffect(() => {
        props.store.loadTEddyEffectP2Results(
            props.store.currentEval?.interaction?.audioMetadataId,
        );
    }, [props.store]);

    const fullControls = props.canSeeTranscript
        ? soundEditorOpen
        : props.store.isMediaViewerDockOpen;

    const shouldTruncateButtonText = useMediaQuery(
        theme.breakpoints.down("lg"),
    );

    return (
        <Observer>
            {() => (
                <AcxDrawer
                    anchor={"bottom"}
                    miniSizePx={drawerMinSize}
                    maxSizePx={
                        audioCollapseOpenCheck
                            ? props.maxHeight
                            : props.maxHeight - 110
                    }
                    customRootStyles={{ zIndex: theme.zIndex.appBar - 2 }}
                    customPaperSx={{
                        boxShadow: "none",
                        borderLeft: `1px solid ${theme.palette.lightgrayBorder.main}`,
                        borderRight: `1px solid ${theme.palette.lightgrayBorder.main}`,
                    }}
                    onDrawerResize={props.store.setMediaDrawerSize}
                    variant={"permanent"}
                    alternateLeftPosition={props.altLeftPosition}
                    alternateWidth={props.altWidth}
                    loading={false}
                    collapsible={
                        Boolean(
                            props.store.evalAudio || props.store.evalChat,
                        ) && props.collapsible
                            ? "mini"
                            : "full"
                    }
                    open={props.store.isMediaViewerDockOpen}
                    onDrawerClose={props.store.closeMediaViewerDrawer}
                    content={
                        <Observer>
                            {() => (
                                <>
                                    <Grid
                                        container
                                        direction="row"
                                        justifyContent={"flex-end"}
                                        alignItems={"stretch"}
                                        style={fullheight}
                                    >
                                        <Grid item xs={12}>
                                            <div style={fullheight}>
                                                {Boolean(
                                                    props.store.evalAudio &&
                                                        props.store
                                                            .mediaType ===
                                                            InteractionType.Audio,
                                                ) && (
                                                    <SoundClipEditorV2
                                                        soundClipEditorStore={
                                                            props.store
                                                                .soundClipEditorStore
                                                        }
                                                        playerOptions={
                                                            playerOptions
                                                        }
                                                        fullControls={
                                                            fullControls
                                                        }
                                                        textButton={
                                                            props.canSeeTranscript
                                                                ? props.store
                                                                      .isMediaViewerDockOpen
                                                                    ? `Hide${
                                                                          shouldTruncateButtonText
                                                                              ? ""
                                                                              : " Transcript"
                                                                      }`
                                                                    : `View${
                                                                          shouldTruncateButtonText
                                                                              ? ""
                                                                              : " Transcript"
                                                                      }`
                                                                : null
                                                        }
                                                        textButtonProps={{
                                                            onClick:
                                                                handleOpenTranscript,
                                                            disabled:
                                                                !props.canSeeTranscript,
                                                        }}
                                                        noSegmentRemoval
                                                        onSegmentMouseOver={
                                                            props.store
                                                                .setActiveClip
                                                        }
                                                        onSegmentClick={
                                                            props.store
                                                                .setOpenClipId
                                                        }
                                                        alternateClipDownloader={
                                                            props.store
                                                                .redactedClipDownloader
                                                        }
                                                        onClipEditorReady={
                                                            props.store
                                                                .setAudioVisualizerReady
                                                        }
                                                        segmentList={
                                                            props.store
                                                                .clipsToSegmentList
                                                        }
                                                        disabled={
                                                            !Boolean(
                                                                props.store
                                                                    .evalAudio,
                                                            )
                                                        }
                                                        onAudioBufferReady={
                                                            props.store
                                                                .setEvalAudioBuffer
                                                        }
                                                        id={
                                                            "eval-audio-interaction-editor"
                                                        }
                                                        generateMp3={false}
                                                        editorHeight={
                                                            editorHeight
                                                        }
                                                        onClipGenerated={
                                                            props.store
                                                                .onAudioClipCreated
                                                        }
                                                        onClipUpdated={
                                                            props.store
                                                                .onAudioClipUpdated
                                                        }
                                                        clip={
                                                            props.store
                                                                .evalAudio
                                                        }
                                                        enableZoom
                                                        iconButton={
                                                            audioCollapseOpenCheck ? (
                                                                <Tooltip title="Minimize audio editor">
                                                                    <ExpandMore />
                                                                </Tooltip>
                                                            ) : (
                                                                <Tooltip title="Maximize audio editor">
                                                                    <ExpandLess fontSize="small" />
                                                                </Tooltip>
                                                            )
                                                        }
                                                        iconButtonProps={{
                                                            onClick:
                                                                soundEditorOpenToggle,
                                                            sx: {
                                                                color: hexToRGB(
                                                                    "#000000",
                                                                    0.54,
                                                                ),
                                                            },
                                                        }}
                                                    />
                                                )}
                                                {Boolean(isProcessedChat) && (
                                                    <AcxChatViewer
                                                        scrollTo={
                                                            props.store
                                                                .activeClipInverse
                                                        }
                                                        records={
                                                            props.store.evalChat
                                                        }
                                                        setFocusClip={
                                                            props.store
                                                                .setActiveClip
                                                        }
                                                        mediaClips={
                                                            props.store
                                                                .currentEval
                                                                ?.activeSoundClips
                                                        }
                                                        onClip={
                                                            props.store
                                                                .onTextMediaClipCreated
                                                        }
                                                        showHeader
                                                        showDivider
                                                        eddyEffectP2Data={
                                                            props.store
                                                                .eddyEffectP2Data
                                                        }
                                                        secondaryButton={
                                                            props.canSeeTranscript
                                                        }
                                                        secondaryButtonLabel={
                                                            props.store
                                                                .isMediaViewerDockOpen
                                                                ? "Minimize Chat"
                                                                : "Expand Chat"
                                                        }
                                                        secondaryButtonOnClick={
                                                            handleOpenTranscript
                                                        }
                                                        showChatUnredactedUI={
                                                            showChatUnredactedUI
                                                        }
                                                        showChatTranslationUI={
                                                            showChatTranslationUI
                                                        }
                                                        translationLanguageCode={
                                                            props.store
                                                                .translationChatLanguageCode
                                                        }
                                                        selectedLanguageCode={
                                                            props.store
                                                                .selectedTranscriptLanguage
                                                        }
                                                        onChangeLanguage={(
                                                            value,
                                                        ) => {
                                                            if (value) {
                                                                props.store.changeTranscriptLanguage(
                                                                    value,
                                                                );
                                                            }
                                                        }}
                                                        languageToggleId="eval-chat-transcript-language-toggle"
                                                    />
                                                )}
                                            </div>
                                        </Grid>
                                    </Grid>

                                    {isAudio && props.canSeeTranscript && (
                                        <Grid
                                            container
                                            direction="row"
                                            justifyContent={"flex-end"}
                                            alignItems={"flex-start"}
                                        >
                                            {props.store.transcriptionData && (
                                                <Grid
                                                    item
                                                    xs={12}
                                                    style={{
                                                        height: "350px",
                                                        width: "100%",
                                                        display: `${
                                                            props.store
                                                                .isMediaViewerDockOpen
                                                                ? "block"
                                                                : "none"
                                                        }`,
                                                    }}
                                                    className="pendo-ignore"
                                                >
                                                    <Scrollbars>
                                                        <AcxTranscription
                                                            disableRedact
                                                            focused={
                                                                props.store
                                                                    .mediaDrawerOpen
                                                            }
                                                            playClip={
                                                                onSelectedClip
                                                            }
                                                            onResultClick={(
                                                                result,
                                                            ) => {
                                                                props.store.soundClipEditorStore.setPlayHeadTime(
                                                                    result.startTime,
                                                                    true,
                                                                );
                                                            }}
                                                            onClip={async (
                                                                start,
                                                                end,
                                                            ) => {
                                                                await props.store.onAudioClipCreated(
                                                                    start,
                                                                    end,
                                                                );
                                                            }}
                                                            eddyEffectP2Data={
                                                                props.store
                                                                    .eddyEffectP2Data
                                                            }
                                                            adverseEventData={[]}
                                                            transcriptWords={
                                                                props.store
                                                                    .transcriptionData
                                                                    .words
                                                            }
                                                            transcriptPhrases={
                                                                props.store
                                                                    .transcriptionData
                                                                    .phrases
                                                            }
                                                            showTranslationUI={
                                                                props.store
                                                                    .translatedTranscript !==
                                                                undefined
                                                            }
                                                            showUnredactedUI={
                                                                props.store
                                                                    .unredactedTranscript !==
                                                                undefined
                                                            }
                                                            isTranslated={
                                                                props.store
                                                                    .translatedTranscript !==
                                                                    undefined &&
                                                                (props.store
                                                                    .selectedTranscriptLanguage ===
                                                                    ENGLISH_LANGUAGE_CODE ||
                                                                    props.store
                                                                        .selectedTranscriptLanguage ===
                                                                        UNREDACTED_ENGLISH_LANGUAGE_CODE)
                                                            }
                                                            translationLanguageCode={
                                                                props.store
                                                                    .translationTranscriptLanguageCode
                                                            }
                                                            onChangeLanguage={(
                                                                value,
                                                            ) => {
                                                                if (value) {
                                                                    props.store.changeTranscriptLanguage(
                                                                        value,
                                                                    );
                                                                }
                                                            }}
                                                            selectedLanguageCode={
                                                                props.store
                                                                    .selectedTranscriptLanguage
                                                            }
                                                            hasEvaluation
                                                            amdId={
                                                                props.store
                                                                    .currentEval
                                                                    ?.interaction
                                                                    ?.audioMetadataId
                                                            }
                                                            speakerStore={
                                                                props.store
                                                                    .speakerStore
                                                            }
                                                        />
                                                    </Scrollbars>
                                                </Grid>
                                            )}
                                        </Grid>
                                    )}
                                </>
                            )}
                        </Observer>
                    }
                />
            )}
        </Observer>
    );
};

export default MediaViewerDock;
